import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "../../i18n";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "⌫"];
export const PIN_LENGTH = 4;

/**
 * Four dots and a keypad: how a person says who they are at the till.
 *
 * Submits by itself on the fourth digit — there is no OK button to find.
 * `onSubmit(pin)` resolves to nothing on success, or to the message to show
 * when the PIN was not accepted, which clears the dots for another try. A
 * counter with a keyboard can type the digits.
 */
const PinPad = ({ onSubmit }) => {
  const { t } = useI18n();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The pad usually closes on success; nothing is set on it after that.
  // Set on every mount, not just initialised: StrictMode mounts twice.
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const submit = async (value) => {
    setBusy(true);
    setError("");

    const problem = await onSubmit(value);

    if (!mounted.current) return;
    if (problem) {
      setError(problem);
      setPin("");
    }
    setBusy(false);
  };

  const press = (key) => {
    if (busy || !key) return;

    if (key === "⌫") {
      setPin((current) => current.slice(0, -1));
      return;
    }

    const next = `${pin}${key}`.slice(0, PIN_LENGTH);
    setPin(next);
    setError("");

    if (next.length === PIN_LENGTH) submit(next);
  };

  useEffect(() => {
    const onKey = (event) => {
      if (/^[0-9]$/.test(event.key)) press(event.key);
      if (event.key === "Backspace") press("⌫");
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <>
      {/* Digits read left to right in every language, so the dots do too */}
      <div className="mt-6 flex justify-center gap-4" dir="ltr" aria-hidden="true">
        {Array.from({ length: PIN_LENGTH }, (_, index) => (
          <span
            key={index}
            className={`h-4 w-4 rounded-full transition-colors ${
              index < pin.length ? "bg-primary-400" : "bg-slate-700"
            }`}
          />
        ))}
      </div>

      <p className="mt-3 h-5 text-center text-sm text-red-400" aria-live="polite">
        {busy ? <span className="text-slate-400">{t("pos.lock.checking")}</span> : error}
      </p>

      <div className="mt-3 w-full max-w-xs grid grid-cols-3 gap-3" dir="ltr">
        {KEYS.map((key, index) => (
          <button
            key={index}
            type="button"
            onClick={() => press(key)}
            disabled={!key || busy}
            aria-label={key === "⌫" ? t("pos.lock.deleteDigit") : undefined}
            className={`h-16 rounded-2xl text-2xl font-semibold transition-all active:scale-95 ${
              key
                ? "bg-slate-800 text-slate-100 hover:bg-slate-700 disabled:opacity-60"
                : "invisible"
            }`}
          >
            {key}
          </button>
        ))}
      </div>
    </>
  );
};

export default PinPad;
